import { createContext, useContext, type ReactNode } from 'react';
import { Tooltip } from '@skupreme/shared/ui';

/**
 * EN-4117: what the orders pages know about the source serving them.
 *
 * The list and detail pages resolve `useOrdersLive` once and hand the answer
 * down, so a popup deep in the tree does not ask again. `isV2` is false until
 * `/live` has answered, and outside a provider, which is the legacy page.
 */
export type OrdersV2State = {
  isV2: boolean;
  /** Query parameters the API said it ignores; only ever filled on V2. */
  notSupported: readonly string[];
};

const LEGACY: OrdersV2State = { isV2: false, notSupported: [] };

const OrdersV2Context = createContext<OrdersV2State>(LEGACY);

export const OrdersV2Provider = OrdersV2Context.Provider;

export const useOrdersV2 = () => useContext(OrdersV2Context);

export const NOT_AVAILABLE_YET = 'Not available yet on the new orders page';

/**
 * The tooltip a write action carries on V2. The action disables itself with
 * `useOrdersV2().isV2`; this only explains why. Off V2 it renders `children`
 * untouched, so a legacy page keeps its exact markup.
 */
export function ReadOnlyTip({
  children,
  triggerClassName,
}: {
  children: ReactNode;
  triggerClassName?: string;
}) {
  const { isV2 } = useOrdersV2();

  if (!isV2) {
    return <>{children}</>;
  }

  return (
    <Tooltip content={NOT_AVAILABLE_YET} triggerClassName={triggerClassName}>
      {children}
    </Tooltip>
  );
}
